/**
 * The twelve skill-FX samples (user-approved prototypes, docs/art/liked/fx-samples-2026-10-11/; ideas from 9yin footage,
 * docs/research/2026-10-11-9yin-skill-fx.md §5, our own shapes and palette), shared by the sample page
 * (tools/fxSamples/skills.ts) and the game's ported skill effects (lane FX-PORT, render/fx/sampleArt/port.ts). Moved here
 * unchanged but for the body: a scene says where the body and its limbs are at a time (`body`, `limbs`), so the page
 * reads its posed rig and the game the fighter it draws. Every effect goes through the FX pen (render/fx/pixelRules.ts
 * `PixelPen`: scene grid, FX ramps, 1-px outline, dither) on a 12 fps stepped clock.
 */
import type { Weapon } from '../../looks/types';
import type { MotionId } from '../../motions';
import { add, blade, crescent, diamond, hash, isoRing, lerp, line, px, R, type G, type V } from './kit';

export const FPS = 12;

const LV = [0, 0.08, 0.2, 1] as const;
const level = (g: G, l: number) => {
  g.globalAlpha = LV[Math.max(0, Math.min(3, Math.round(l)))]!;
};
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
export const since = (t: number, at: number) => Math.floor((t - at) * FPS + 1e-6);
const C = (ramp: string, i: number): string => R[ramp]![Math.max(0, Math.min(R[ramp]!.length - 1, i))]!;

// ── Scene ──

export interface Act {
  m: MotionId;
  t0: number;
  t1: number;
}
/** Where a sample plays: its clock (s), the caster's and the foe's feet, the unit direction between, the chest height. */
export interface Scene {
  t: number;
  a: V;
  f: V;
  d: V;
  chest: number;
  sample: Sample;
  /** Where the body stands at time t (the dash, the leap). */
  body(t: number): V;
  /** The weapon hand, the other hand and the weapon's tip at time t. */
  limbs(t: number): { hand: V; handB: V; tip: V };
  /** No hit marks (a plain blow in a duel bout: the small spark only, render/fx/duelHits.ts). */
  quiet?: boolean;
  /** The top of the stage (the buff pillar's top); 0 on the sample page. */
  top?: number;
}
export interface Sample {
  id: string;
  th: string;
  weapon: Weapon;
  acts: Act[];
  /** Blows that land (s): the foe jolts and flashes, a big one shakes the stage. */
  hits: number[];
  big?: number;
  /** Where the body stands, off its start (the dash, the leap). */
  move?(s: Scene, t: number): V;
  /** Times to leave split-body afterimages at. */
  ghosts?: number[];
  under?(g: G, s: Scene): void;
  over(g: G, s: Scene): void;
}

// ── Shared pieces ──

/** A trail through the weapon tip's last positions: the fresh part 2 deep and solid, the tail thin and dithered. */
function tipTrail(g: G, s: Scene, from: number, to: number, ramp: string, n = 6): void {
  if (s.t < from || s.t >= to) return;
  const pts: V[] = [];
  for (let i = 0; i <= n; i++) {
    const ti = s.t - i / (FPS * 2);
    if (ti < from - 0.1) break;
    pts.push(s.limbs(ti).tip);
  }
  for (let i = pts.length - 2; i >= 0; i--) {
    const p = pts[i]!;
    const q = pts[i + 1]!;
    level(g, i < 2 ? 3 : i < 4 ? 2 : 1);
    if (i < 3) line(g, [p[0], p[1] + 1], [q[0], q[1] + 1], C(ramp, 3));
    line(g, p, q, i < 2 ? C('white', 8) : C(ramp, 5));
  }
  g.globalAlpha = 1;
}

/** A 4-ray star with diagonals and a diamond core, two frames. */
function starOf(g: G, p: V, e: number, ramp: string, big = 1): void {
  if (e < 0 || e > 1) return;
  const L = Math.round((e === 0 ? 10 : 6) * big);
  const D = Math.round((e === 0 ? 5 : 3) * big);
  level(g, e === 0 ? 3 : 2);
  line(g, [p[0] - L, p[1]], [p[0] + L, p[1]], C('white', 8));
  line(g, [p[0], p[1] - L + 2], [p[0], p[1] + L - 2], C('white', 8));
  for (const [dx, dy] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const)
    line(g, [p[0] + dx * 2, p[1] + dy * 2], [p[0] + dx * D, p[1] + dy * D], C(ramp, 4));
  diamond(g, p[0], p[1], e === 0 ? 3 : 2, C(ramp, 4), C('white', 8));
  g.globalAlpha = 1;
}

/** Puffs of dust (or smoke) in an iso ring, flung out and dithered away over `life` frames. */
function puffs(g: G, c: V, e: number, n: number, r0: number, speed: number, ramp: string, life = 7, rise = 0): void {
  if (e < 0 || e >= life) return;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(i, 5) * 0.6;
    const r = r0 + e * speed * (0.7 + hash(i, 7) * 0.6);
    const x = c[0] + Math.cos(a) * r;
    const y = c[1] + Math.sin(a) * r * 0.5 - e * rise;
    const big = e < life / 2;
    level(g, e < life * 0.4 ? 3 : e < life * 0.7 ? 2 : 1);
    g.fillStyle = C(ramp, big ? 5 : 4);
    g.fillRect(Math.round(x) - 1, Math.round(y) - 1, big ? 3 : 2, big ? 2 : 1);
    if (big) px(g, x, y - 1, C(ramp, 6));
  }
  g.globalAlpha = 1;
}

/** An iso sector (ground fan) round c from angle a0 to a1, radius r: a dithered fill, a 2-cell rim. */
function sector(g: G, c: V, r: number, a0: number, a1: number, fill: string, rim: string, fl = 1): void {
  for (let y = Math.floor(c[1] - r / 2); y <= c[1] + r / 2; y++)
    for (let x = Math.floor(c[0] - r); x <= c[0] + r; x++) {
      const u = x - c[0];
      const v = (y - c[1]) * 2;
      const d = Math.hypot(u, v);
      if (d > r) continue;
      const a = Math.atan2(v, u);
      if (a < a0 || a > a1) continue;
      level(g, fl);
      px(g, x, y, fill);
    }
  level(g, 3);
  isoRing(g, c[0], c[1], r, rim, true, a0, a1);
  line(g, c, [c[0] + Math.cos(a0) * r, c[1] + Math.sin(a0) * r * 0.5], rim);
  line(g, c, [c[0] + Math.cos(a1) * r, c[1] + Math.sin(a1) * r * 0.5], rim);
  g.globalAlpha = 1;
}

/** A thick band on the ground ellipse from angle a0 to a1 (rows inward), the head end the brightest. */
function groundBand(g: G, c: V, r: number, a0: number, a1: number, rows: number, core: string, edge: string): void {
  if (a1 <= a0) return;
  isoRing(g, c[0], c[1], r, core, false, a0, a1);
  for (let k = 1; k < rows; k++) isoRing(g, c[0], c[1], r - k, edge, false, a0, a1);
}

// ── The twelve ──

export const strikeAt = (s: Scene, k = 1): V => [s.f[0] - s.d[0] * 3, s.f[1] - s.chest * k];

export const SAMPLES: Sample[] = [
  {
    id: 'spin',
    th: '1 ฟันหมุนรอบตัว (กระบี่) — ทองส้ม',
    weapon: 'sword',
    acts: [{ m: 'snowSweep', t0: 0.2, t1: 1.1 }],
    hits: [0.85],
    big: 0.85,
    under(g, s) {
      const c = s.a;
      const t0 = 0.55;
      const e = since(s.t, t0);
      if (e < 0 || e > 14) return;
      const r = 30;
      const k = clamp01((e + 1) / 5);
      const start = -Math.PI * 0.6;
      const head = start + k * Math.PI * 2;
      if (e < 5) {
        // The sweep: the fresh 70 degrees thick and bright, older part thinner, the rest dithered.
        level(g, 3);
        groundBand(g, c, r, Math.max(start, head - 1.2), head, 3, C('gold', 6), C('fire', 4));
        level(g, 2);
        groundBand(g, c, r, start, Math.max(start, head - 1.2), 2, C('gold', 5), C('fire', 3));
      } else {
        const f = e - 5;
        level(g, f < 3 ? 3 : f < 6 ? 2 : 1);
        groundBand(g, c, r + f * 2, 0, Math.PI * 2, f < 3 ? 2 : 1, C('gold', f < 3 ? 6 : 5), C('fire', 4));
        level(g, 1);
        isoRing(g, c[0], c[1], r - 4, C('fire', 3), false);
      }
      g.globalAlpha = 1;
    },
    over(g, s) {
      const c = s.a;
      const e = since(s.t, 0.55);
      if (e >= 0 && e < 5) {
        const k = clamp01((e + 1) / 5);
        const head = -Math.PI * 0.6 + k * Math.PI * 2;
        const p: V = [c[0] + Math.cos(head) * 30, c[1] + Math.sin(head) * 15];
        diamond(g, p[0], p[1] - 1, 2, C('gold', 5), C('white', 8));
      }
      // Leaf-petals flung off the arc, turning as they fly, falling a little, dithered out.
      if (e >= 1 && e < 16)
        for (let i = 0; i < 16; i++) {
          const born = 1 + Math.floor(i / 4);
          const age = e - born;
          if (age < 0 || age > 9) continue;
          const a = -Math.PI * 0.6 + ((i * 2.3) % (Math.PI * 2));
          const r = 30 + age * (2.5 + hash(i, 2) * 2);
          const x = c[0] + Math.cos(a) * r + age * hash(i, 4) * 1.5;
          const y = c[1] + Math.sin(a) * r * 0.5 - 6 - hash(i, 3) * 10 + age * 0.8;
          level(g, age < 4 ? 3 : age < 7 ? 2 : 1);
          const col = i % 3 === 0 ? C('gold', 5) : i % 3 === 1 ? C('fire', 4) : C('fire', 5);
          const turn = (age + i) % 3;
          g.fillStyle = col;
          if (turn === 0) g.fillRect(Math.round(x), Math.round(y), 2, 1);
          else if (turn === 1) g.fillRect(Math.round(x), Math.round(y), 1, 2);
          else {
            g.fillRect(Math.round(x), Math.round(y), 1, 1);
            g.fillRect(Math.round(x) + 1, Math.round(y) + 1, 1, 1);
          }
          px(g, x, y, C('fire', 2));
        }
      // Sparks off the head.
      if (e >= 0 && e < 7)
        for (let i = 0; i < 5; i++) {
          const a = hash(i, e) * Math.PI * 2;
          const p: V = [c[0] + Math.cos(a) * (28 + e * 2), c[1] + Math.sin(a) * 14 - 4 - e];
          level(g, e < 4 ? 3 : 2);
          px(g, p[0], p[1], C('white', 8));
        }
      g.globalAlpha = 1;
      tipTrail(g, s, 0.5, 1.0, 'gold');
      hitStar(g, s, strikeAt(s), since(s.t, 0.85), 'gold');
    },
  },
  {
    id: 'snow',
    th: '2 กระบี่ธารเยือกแข็งเฉือน (กระบี่) — ฟ้าขาว',
    weapon: 'sword',
    acts: [
      { m: 'dash', t0: 0.4, t1: 0.7 },
      { m: 'snowCleave', t0: 0.7, t1: 1.2 },
    ],
    hits: [0.95],
    big: 0.95,
    ghosts: [0.5, 0.58, 0.66],
    move(s, t) {
      const k = clamp01((t - 0.45) / 0.25) * (1 - clamp01((t - 1.7) / 0.4));
      return [s.d[0] * 52 * k, s.d[1] * 52 * k];
    },
    under(g, s) {
      // The skid: a dark streak along the dash, fading by dither.
      const e = since(s.t, 0.5);
      if (e < 0 || e > 14) return;
      const k = clamp01((s.t - 0.45) / 0.25);
      level(g, e < 6 ? 2 : 1);
      const b: V = add(s.a, s.d, 52 * k);
      line(g, [s.a[0], s.a[1] + 1], [b[0], b[1] + 1], C('ink', 3));
      line(g, [s.a[0] + 6, s.a[1] - 1], [b[0] - 4, b[1] - 1], C('ink', 4));
      g.globalAlpha = 1;
    },
    over(g, s) {
      // Sheared ribbons: three long flat streaks blown past along the dash, their tails catching up.
      const e = since(s.t, 0.55);
      if (e >= 0 && e < 8)
        for (let i = 0; i < 3; i++) {
          const y = s.a[1] - s.chest + (i - 1) * 6 + i;
          const head = s.a[0] + Math.min(1, (e + 1) / 3) * 100 + i * 4;
          const tail = s.a[0] - 10 + Math.max(0, e - 2) * 22 + i * 6;
          if (head - tail < 3) continue;
          level(g, e < 4 ? 3 : e < 6 ? 2 : 1);
          blade(g, [tail, y], [head, y - 2 + i], i === 1 ? 3 : 2, C('white', 8), C('azure', 4), 0.75);
        }
      g.globalAlpha = 1;
      tipTrail(g, s, 0.8, 1.15, 'azure');
      hitStar(g, s, strikeAt(s), since(s.t, 0.95), 'azure', 1.2);
      // Frost glints falling where the cut landed.
      const f = since(s.t, 0.95);
      if (f >= 0 && f < 9)
        for (let i = 0; i < 6; i++) {
          const x = s.f[0] + (hash(i, 1) - 0.5) * 24;
          const y = s.f[1] - s.chest - 6 + hash(i, 2) * 10 + f * 1.5;
          level(g, f < 4 ? 3 : 2);
          px(g, x, y, i % 2 ? C('white', 8) : C('azure', 5));
        }
      g.globalAlpha = 1;
    },
  },
  {
    id: 'comet',
    th: '3 แทงดาวตก (กระบี่) — แดงเพลิง',
    weapon: 'sword',
    acts: [{ m: 'wxThrust', t0: 0.1, t1: 1.3 }],
    hits: [0.8],
    big: 0.8,
    under(g, s) {
      // The sunburst: radial spikes on the ground round the feet, growing on the wind-up, flashing on the thrust.
      if (s.t < 0.2 || s.t > 1.7) return;
      const grow = clamp01((s.t - 0.2) / 0.5);
      const e = since(s.t, 0.8);
      const fade = e < 0 ? 3 : e < 6 ? 3 : e < 9 ? 2 : 1;
      const n = 12;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + (e >= 0 ? 0.1 : 0);
        const L = (i % 2 ? 8 : 14) * grow + (e >= 0 && e < 3 ? 4 : 0);
        const p0: V = [s.a[0] + Math.cos(a) * 8, s.a[1] + Math.sin(a) * 4];
        const p1: V = [s.a[0] + Math.cos(a) * (8 + L), s.a[1] + Math.sin(a) * (8 + L) * 0.5];
        level(g, fade);
        line(g, p0, p1, i % 2 ? C('fire', 3) : C('maroon', 4));
        if (!(i % 2)) line(g, [p0[0], p0[1] + 1], [p1[0], p1[1] + 1], C('fire', 2));
      }
      level(g, fade);
      isoRing(g, s.a[0], s.a[1], 8, C('fire', 4), true);
      g.globalAlpha = 1;
    },
    over(g, s) {
      // The comet: a cone driven from the blade tip through the foe, three frames, then embers.
      const e = since(s.t, 0.72);
      const tip = s.limbs(Math.min(s.t, 0.8)).tip;
      if (e >= 0 && e < 3) {
        const end: V = [s.f[0] + 22, tip[1]];
        const head = lerp(tip, end, Math.min(1, (e + 1) / 2));
        const n = Math.round(head[0] - tip[0]);
        // A comet: a bright blunt head at the front, a body tapering back to a point at the blade tip.
        const body = Math.min(n, 44);
        for (let i = 0; i <= body; i++) {
          const u = 1 - i / Math.max(1, body); // 1 at the head, 0 at the tail
          const w = Math.round(7 * Math.pow(u, 0.8) + (u > 0.04 ? 0.5 : 0));
          const x = Math.round(head[0] - i);
          if (x < tip[0]) break;
          for (let k = -w; k <= w; k++) {
            const col = Math.abs(k) <= w / 3 ? (u > 0.75 ? C('white', 8) : C('gold', 6)) : Math.abs(k) <= (2 * w) / 3 ? C('fire', 4) : C('maroon', 4);
            level(g, u < 0.3 && e === 2 ? 2 : 3);
            px(g, x, tip[1] + k, col);
          }
        }
        // Thin flame streaks trailing the cone back to the blade.
        level(g, 2);
        for (const dy of [-3, 3]) line(g, [tip[0], tip[1] + dy * 0.4], [head[0] - body * 0.5, tip[1] + dy], C('fire', 3));
      }
      // Embers drifting up off the comet's path.
      const f = since(s.t, 0.95);
      if (f >= 0 && f < 10)
        for (let i = 0; i < 10; i++) {
          const x = lerp(tip, [s.f[0] + 20, tip[1]], hash(i, 8))[0];
          const y = tip[1] + (hash(i, 9) - 0.5) * 6 - f * 1.5;
          level(g, f < 4 ? 3 : f < 7 ? 2 : 1);
          px(g, x, y, i % 2 ? C('fire', 5) : C('gold', 5));
        }
      g.globalAlpha = 1;
      hitStar(g, s, strikeAt(s), since(s.t, 0.8), 'fire', 1.2);
    },
  },
  {
    id: 'palm',
    th: '4 ฝ่ามือทองคำ (มือเปล่า) — ทอง',
    weapon: 'none',
    acts: [{ m: 'wxPalm', t0: 0.1, t1: 1.4 }],
    hits: [0.9],
    big: 0.9,
    under(g, s) {
      // The glyph circle: two iso rings and eight tick-glyphs between them, drawn on in steps, turning.
      if (s.t < 0.15 || s.t > 1.5) return;
      const e = since(s.t, 0.9);
      const lvl = e < 0 ? 3 : e < 4 ? 3 : e < 7 ? 2 : 1;
      const k = clamp01((s.t - 0.15) / 0.3);
      const rot = s.t * 0.8;
      level(g, lvl);
      isoRing(g, s.a[0], s.a[1], 24, C('gold', 5), true, rot, rot + Math.PI * 2 * k);
      if (s.t > 0.4) isoRing(g, s.a[0], s.a[1], 17, C('gold', 4), false);
      if (s.t > 0.5)
        for (let i = 0; i < 8; i++) {
          const a = rot + (i * Math.PI) / 4;
          const c: V = [s.a[0] + Math.cos(a) * 20.5, s.a[1] + Math.sin(a) * 10.25];
          level(g, lvl);
          g.fillStyle = C('gold', 6);
          // A small glyph: a bar and a tick, its own per slot.
          g.fillRect(Math.round(c[0]) - 1, Math.round(c[1]), 3, 1);
          g.fillRect(Math.round(c[0]) + (i % 2 ? -1 : 1), Math.round(c[1]) - 1, 1, 1);
        }
      g.globalAlpha = 1;
    },
    over(g, s) {
      const e = since(s.t, 0.9);
      const { hand } = s.limbs(Math.min(s.t, 0.9));
      // The palm flash: a gold disc and rays at the palm on the push.
      if (e >= -1 && e < 2) {
        const r = e < 0 ? 2 : e === 0 ? 4 : 3;
        level(g, e < 1 ? 3 : 2);
        diamond(g, hand[0] + 2, hand[1], r, C('gold', 5), C('white', 8));
        if (e >= 0)
          for (let i = 0; i < 8; i++) {
            const a = (i * Math.PI) / 4;
            line(g, [hand[0] + 2 + Math.cos(a) * (r + 1), hand[1] + Math.sin(a) * (r + 1)], [hand[0] + 2 + Math.cos(a) * (r + 4), hand[1] + Math.sin(a) * (r + 4)], C('gold', 6));
          }
      }
      // The shockwave: a flat gold line skimming the ground from the caster to the foe.
      if (e >= 0 && e < 4) {
        level(g, e < 2 ? 3 : 2);
        const y = s.a[1] - 1;
        blade(g, [s.a[0] + 8, y], [s.f[0] + 10 + e * 4, y], e < 2 ? 3 : 2, C('gold', 6), C('ochre', 3), 0.85);
      }
      g.globalAlpha = 1;
      puffs(g, s.f, e, 10, 6, 3.5, 'brown', 9, 0.5);
      hitStar(g, s, strikeAt(s), e, 'gold', 1.1);
    },
  },
  {
    id: 'staff',
    th: '5 กระบองฟาดพื้น (กระบอง) — ขาวทอง',
    weapon: 'staff',
    acts: [{ m: 'wxSlam', t0: 0.1, t1: 1.4 }],
    hits: [0.9],
    big: 0.9,
    under(g, s) {
      // Cracks where the staff lands, fading by dither.
      const e = since(s.t, 0.9);
      if (e < 0 || e > 14) return;
      const p = add(s.a, s.d, 26);
      level(g, e < 8 ? 3 : e < 11 ? 2 : 1);
      for (let i = 0; i < 6; i++) {
        let q: V = p;
        const a = (i / 6) * Math.PI * 2 + 0.3;
        for (let j = 1; j <= 3; j++) {
          const n: V = [p[0] + Math.cos(a + (hash(i, j) - 0.5) * 0.6) * j * 5, p[1] + Math.sin(a) * j * 2.5];
          line(g, q, n, C('ink', 2));
          q = n;
        }
      }
      level(g, e < 4 ? 3 : 2);
      isoRing(g, p[0], p[1], 4 + Math.min(e, 4) * 3, C('gold', 5), e < 3);
      g.globalAlpha = 1;
    },
    over(g, s) {
      tipTrail(g, s, 0.4, 1.0, 'gold', 8);
      // The staff is brown on brown ground: a pale gold edge along it while it swings and lands.
      if (s.t >= 0.35 && s.t < 1.6) {
        const { hand, tip } = s.limbs(s.t);
        const back: V = [hand[0] + (hand[0] - tip[0]) * 0.3, hand[1] + (hand[1] - tip[1]) * 0.3];
        level(g, 3);
        line(g, [back[0] + 1, back[1]], [tip[0] + 1, tip[1]], C('gold', 5));
        level(g, 2);
        line(g, [back[0] - 1, back[1]], [tip[0] - 1, tip[1]], C('white', 8));
        g.globalAlpha = 1;
      }
      const e = since(s.t, 0.9);
      const p = add(s.a, s.d, 26);
      // Sparks thrown up and falling back.
      if (e >= 0 && e < 8)
        for (let i = 0; i < 12; i++) {
          const a = -Math.PI * (0.1 + 0.8 * hash(i, 3));
          const v = 2 + hash(i, 4) * 2.5;
          const x = p[0] + Math.cos(a) * v * e * 1.6;
          const y = p[1] + Math.sin(a) * v * e + 0.35 * e * e;
          level(g, e < 4 ? 3 : e < 6 ? 2 : 1);
          px(g, x, y, i % 3 ? C('gold', 6) : C('fire', 4));
          if (e < 3) px(g, x, y + 1, C('fire', 3));
        }
      g.globalAlpha = 1;
      puffs(g, p, e, 12, 6, 3, 'brown', 9, 0.4);
      hitStar(g, s, [p[0], p[1] - 3], e, 'gold', 1);
    },
  },
  {
    id: 'fist',
    th: '6 หมัดเปลว (มือเปล่า) — ทองทราย',
    weapon: 'none',
    acts: [
      { m: 'thrust', t0: 0.1, t1: 0.45 },
      { m: 'thrust', t0: 0.45, t1: 0.8 },
      { m: 'thrust', t0: 0.8, t1: 1.15 },
      { m: 'wxStomp', t0: 1.15, t1: 2.2 },
    ],
    hits: [0.3, 0.65, 1.0, 1.65],
    big: 1.65,
    under(g, s) {
      // The stomp's sand wave: thick ochre arcs rolling out on the ground.
      const e = since(s.t, 1.65);
      if (e < 0 || e > 9) return;
      for (let j = 0; j < 2; j++) {
        const f = e - j * 2;
        if (f < 0) continue;
        level(g, f < 3 ? 3 : f < 6 ? 2 : 1);
        isoRing(g, s.a[0], s.a[1], 12 + f * 8, C(j ? 'ochre' : 'brown', 5), f < 5);
        if (f < 4) isoRing(g, s.a[0], s.a[1], 10 + f * 8, C('gold', 5), false);
      }
      g.globalAlpha = 1;
    },
    over(g, s) {
      // A flare at the fist on each punch.
      for (const h of [0.3, 0.65, 1.0]) {
        const e = since(s.t, h);
        if (e < 0 || e > 1) continue;
        const { hand } = s.limbs(h);
        level(g, e === 0 ? 3 : 2);
        diamond(g, hand[0] + 2, hand[1], e === 0 ? 6 : 4, C('gold', 5), C('white', 8));
        isoRing(g, hand[0] + 4, hand[1], 6 + e * 4, C('gold', 6), e === 0);
        for (const dy of [-4, -2, 0, 2, 4]) line(g, [hand[0] + 8, hand[1] + dy], [hand[0] + 20 - Math.abs(dy), hand[1] + dy * 1.6], C('gold', 6));
        for (const dy of [-3, 3]) line(g, [hand[0] - 8, hand[1] + dy], [hand[0] - 20, hand[1] + dy], C('ochre', 4));
      }
      g.globalAlpha = 1;
      puffs(g, s.a, since(s.t, 1.65), 20, 10, 5.5, 'ochre', 10, 0.9);
      for (const h of [0.3, 0.65, 1.0]) hitStar(g, s, strikeAt(s), since(s.t, h), 'gold', 1.2);
      hitStar(g, s, strikeAt(s, 0.6), since(s.t, 1.65), 'gold', 1.6);
    },
  },
  {
    id: 'sabre',
    th: '7 ดาบวงพายุ (ดาบ) — ม่วงชมพู',
    weapon: 'sabre',
    acts: [
      { m: 'fan', t0: 0.15, t1: 0.75 },
      { m: 'fan', t0: 0.75, t1: 1.35 },
    ],
    hits: [0.7, 1.3],
    big: 1.3,
    under(g, s) {
      waistRings(g, s, false);
    },
    over(g, s) {
      waistRings(g, s, true);
      tipTrail(g, s, 0.2, 1.35, 'violet');
      // Crescents flung out at the foe on each turn.
      for (const h of [0.6, 1.2]) {
        const e = since(s.t, h);
        if (e < 0 || e > 3) continue;
        const c = lerp([s.a[0] + 14, s.a[1] - s.chest], strikeAt(s), Math.min(1, (e + 1) / 2));
        level(g, e < 2 ? 3 : 2);
        crescent(g, c[0] - 8, c[1], 8, -1.0, 1.0, 3, C('blossom', 5), C('violet', 3), 1, false);
      }
      g.globalAlpha = 1;
      for (const h of [0.7, 1.3]) hitStar(g, s, strikeAt(s), since(s.t, h), 'blossom', 0.9);
    },
  },
  {
    id: 'needle',
    th: '8 เข็มพิษ (มือเปล่า) — เขียวหยก',
    weapon: 'none',
    acts: [{ m: 'qi', t0: 0.2, t1: 0.9 }],
    hits: [0.75],
    under(g, s) {
      // A poison stain spreading under the foe.
      const e = since(s.t, 0.8);
      if (e < 0 || e > 16) return;
      const r = Math.min(15, 5 + e * 3);
      for (let y = -r; y <= r; y++)
        for (let x = -r * 2; x <= r * 2; x++) {
          const d = Math.hypot(x / 2, y) / r;
          if (d > 1 - hash(x + 50, y + 50) * 0.25) continue;
          level(g, e < 10 ? 3 : 1);
          px(g, s.f[0] + x, s.f[1] + Math.round(y / 2), d > 0.75 ? C('leaf', 3) : C('leaf', 2));
        }
      g.globalAlpha = 1;
    },
    over(g, s) {
      // Five needles fanned from the hand, staggered, each a glint head and a green tail.
      const { hand } = s.limbs(0.5);
      for (let i = 0; i < 5; i++) {
        const t0 = 0.5 + i * 0.03;
        const k = clamp01((s.t - t0) / 0.25);
        if (s.t < t0 || k >= 1) continue;
        const end: V = [s.f[0] - 2 + (i - 2), s.f[1] - s.chest + (i - 2) * 4];
        const p = lerp(hand, end, k);
        const q = lerp(hand, end, Math.max(0, k - 0.12));
        const q2 = lerp(hand, end, Math.max(0, k - 0.3));
        line(g, q2, p, C('leaf', 3));
        line(g, [q[0], q[1] - 1], [p[0], p[1] - 1], C('leaf', 5));
        line(g, q, p, C('jade', 5));
        px(g, p[0], p[1], C('white', 8));
        px(g, p[0] + 1, p[1], C('white', 8));
      }
      // Glints on the hits, then poison puffs rising.
      const e = since(s.t, 0.75);
      if (e >= 0 && e < 2)
        for (let i = 0; i < 5; i++) {
          const p: V = [s.f[0] - 2 + (i - 2), s.f[1] - s.chest + (i - 2) * 4];
          hitStar(g, s, p, e, 'jade', 0.9);
        }
      if (e >= 1 && e < 16)
        for (let i = 0; i < 12; i++) {
          const b = e - 1 - (i % 4);
          if (b < 0 || b > 10) continue;
          const x = s.f[0] + (hash(i, 3) - 0.5) * 26 + Math.sin(b * 0.7 + i) * 2;
          const y = s.f[1] - 4 - hash(i, 4) * s.chest - b * 1.5;
          level(g, b < 4 ? 3 : b < 7 ? 2 : 1);
          g.fillStyle = C('jade', b < 4 ? 4 : 3);
          g.fillRect(Math.round(x) - 1, Math.round(y) - 1, b < 5 ? 5 : 3, b < 5 ? 4 : 3);
          px(g, x, y, C('jade', 5));
          px(g, x - 1, y - 1, C('white', 7));
        }
      g.globalAlpha = 1;
    },
  },
  {
    id: 'pillar',
    th: '9 เสาแสงกลีบ (บัฟ) — ชมพูขาว',
    weapon: 'none',
    acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
    hits: [],
    under(g, s) {
      petalRing(g, s, false);
    },
    over(g, s) {
      petalRing(g, s, true);
      // The pillar on the peak: a stepped column from the top of the frame to the feet, narrowing as it goes.
      const e = since(s.t, 1.0);
      if (e >= 0 && e < 9) {
        const w = [7, 11, 9, 7, 5, 4, 3, 2, 1][e]!;
        const x = s.a[0];
        for (let k = -w; k <= w; k++) {
          const col = Math.abs(k) <= w / 3 ? C('white', 8) : Math.abs(k) <= (2 * w) / 3 ? C('blossom', 4) : C('blossom', 3);
          level(g, e < 4 ? (Math.abs(k) === w ? 2 : 3) : e < 7 ? 2 : 1);
          line(g, [x + k, s.top ?? 0], [x + k, s.a[1] - 2], col);
        }
        level(g, e < 4 ? 3 : 2);
        isoRing(g, x, s.a[1], 12 + e * 4, C('blossom', 5), e < 5);
        isoRing(g, x, s.a[1], 8 + e * 3, C('white', 8), false);
      }
      g.globalAlpha = 1;
    },
  },
  {
    id: 'zone',
    th: '10 เขตพัด (กระบี่) — หยกทอง',
    weapon: 'sword',
    acts: [{ m: 'wxQi', t0: 0.1, t1: 1.3 }],
    hits: [0.8, 1.0, 1.2],
    big: 0.8,
    under(g, s) {
      // The fan zone on the ground ahead, drawn out in three steps, held, dithered away.
      const e = since(s.t, 0.65);
      if (e < 0 || e > 16) return;
      const r = e < 1 ? 26 : e < 2 ? 40 : 52;
      const dir = Math.atan2(s.d[1] * 2, s.d[0]);
      sector(g, [s.a[0] + 4, s.a[1]], r, dir - 0.55, dir + 0.55, C('jade', 3), C('jade', 5), e < 10 ? 2 : 1);
    },
    over(g, s) {
      tipTrail(g, s, 0.55, 0.95, 'jade');
      // Crackle: stepped zigzags over the zone, a few per frame, new every frame.
      const e = since(s.t, 0.75);
      if (e >= 0 && e < 8) {
        const fr = Math.floor(s.t * FPS);
        for (let i = 0; i < 3; i++) {
          const a = (hash(i, fr) - 0.5) * 0.9;
          const r0 = 10 + hash(i + 9, fr) * 20;
          let p: V = [s.a[0] + 4 + Math.cos(a) * r0, s.a[1] + Math.sin(a) * r0 * 0.5];
          level(g, 3);
          for (let j = 0; j < 4; j++) {
            const n: V = [p[0] + 4 + hash(i * 5 + j, fr) * 4, p[1] + (hash(j, i + fr) - 0.5) * 6];
            line(g, p, n, j % 2 ? C('ochre', 4) : C('white', 8));
            p = n;
          }
        }
      }
      g.globalAlpha = 1;
      for (const h of [0.8, 1.0, 1.2]) hitStar(g, s, strikeAt(s, 0.4), since(s.t, h), 'ochre', 0.7);
    },
  },
  {
    id: 'leap',
    th: '11 เหินฟาด (ดาบ, ชิงกง) — ม่วง',
    weapon: 'sabre',
    acts: [{ m: 'leap', t0: 0.15, t1: 1.0 }],
    hits: [0.95],
    big: 0.95,
    ghosts: [0.5, 0.62, 0.74],
    move(s, t) {
      const k = clamp01((t - 0.4) / 0.55);
      const back = 1 - clamp01((t - 1.8) / 0.4);
      const x = 54 * k * back;
      const y = -Math.sin(Math.PI * k) * 18 * back;
      return [s.d[0] * x, s.d[1] * x + y];
    },
    under(g, s) {
      // The landing pool: a violet disc spreading on the ground, dithered, and a ring.
      const e = since(s.t, 0.95);
      if (e < 0 || e > 14) return;
      const c = add(s.a, s.d, 54);
      const r = Math.min(22, 10 + e * 4);
      for (let y = -r; y <= r; y++)
        for (let x = -r; x <= r; x++) {
          if (Math.hypot(x, y * 2) > r) continue;
          level(g, e < 6 ? 2 : 1);
          px(g, c[0] + x, c[1] + y, C('violet', Math.hypot(x, y * 2) > r - 3 ? 4 : 2));
        }
      level(g, e < 4 ? 3 : 2);
      isoRing(g, c[0], c[1], r + 2 + e, C('violet', 5), e < 4);
      g.globalAlpha = 1;
    },
    over(g, s) {
      // The leap's arc trail through the air: a crescent band along the body's path.
      const e = since(s.t, 0.4);
      if (e >= 0 && e < 10) {
        for (let i = 0; i < 10; i++) {
          const t = 0.4 + (i / 10) * Math.min(0.55, s.t - 0.4);
          const p = add(s.body(t), [0, -s.chest]);
          const age = since(s.t, t);
          level(g, age < 3 ? 3 : age < 6 ? 2 : 1);
          px(g, p[0], p[1], C('violet', 5));
          px(g, p[0], p[1] + 1, C('violet', 3));
        }
      }
      g.globalAlpha = 1;
      tipTrail(g, s, 0.75, 1.05, 'violet');
      const c = add(s.a, s.d, 54);
      puffs(g, c, since(s.t, 0.95), 10, 10, 2.5, 'ink', 10, 1);
      hitStar(g, s, strikeAt(s), since(s.t, 0.95), 'violet', 1.2);
    },
  },
  {
    id: 'ink',
    th: '12 หมึกสาด (กระบี่) — หมึกแดง',
    weapon: 'sword',
    acts: [{ m: 'wxBreak', t0: 0.1, t1: 1.5 }],
    hits: [0.86],
    big: 0.86,
    under(g, s) {
      // The ink stain under the foe: ragged, dark, lingering, then dithered.
      const e = since(s.t, 0.86);
      if (e < 0 || e > 16) return;
      const r = Math.min(12, 5 + e * 3);
      for (let y = -r; y <= r; y++)
        for (let x = -r * 2; x <= r * 2; x++) {
          const d = Math.hypot(x / 2, y) / r;
          if (d > 1 - hash(x + 77, y + 33) * 0.35) continue;
          level(g, e < 10 ? 3 : e < 13 ? 2 : 1);
          px(g, s.f[0] - 6 + x, s.f[1] + Math.round(y / 2), d > 0.7 ? C('maroon', 2) : C('ink', 1));
        }
      g.globalAlpha = 1;
    },
    over(g, s) {
      // The brush stroke on the chop: an ink crescent with a maroon edge along the blade's fall.
      const e = since(s.t, 0.78);
      if (e >= 0 && e < 4) {
        const c: V = [s.a[0] + 6, s.a[1] - s.chest - 2];
        level(g, e < 2 ? 3 : e < 3 ? 2 : 1);
        crescent(g, c[0], c[1], 20, -1.4, Math.min(1.0, -1.4 + (e + 1) * 0.9), 4, C('ink', 1), C('maroon', 4), 1, true);
      }
      // Ink flecks flung past the foe.
      const f = since(s.t, 0.86);
      if (f >= 0 && f < 8)
        for (let i = 0; i < 12; i++) {
          const a = -0.6 + hash(i, 1) * 1.4;
          const v = 3 + hash(i, 2) * 3;
          const x = s.f[0] + Math.cos(a) * v * f;
          const y = s.f[1] - s.chest + Math.sin(a) * v * f * 0.6 + 0.4 * f * f;
          level(g, f < 4 ? 3 : 2);
          g.fillStyle = i % 4 ? C('ink', 1) : C('maroon', 4);
          g.fillRect(Math.round(x), Math.round(y), i % 3 ? 1 : 2, 1);
        }
      g.globalAlpha = 1;
      hitStar(g, s, strikeAt(s), f, 'maroon', 1);
    },
  },
];

/** Sample 7's rings: flat comet rings turning round the waist, back half behind the figure. */
function waistRings(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.2 || s.t > 1.5) return;
  const b = s.body(s.t);
  const c: V = [b[0], b[1] - s.chest * 0.9];
  for (let j = 0; j < 2; j++) {
    const phi = (s.t - 0.2) * Math.PI * 4 + j * Math.PI;
    for (let i = 14; i >= 0; i--) {
      const a = phi - i * 0.16;
      if (Math.sin(a) > 0 !== front) continue;
      const rx = 15 + j * 3;
      const p: V = [c[0] + Math.cos(a) * rx, c[1] + j * 3 + Math.sin(a) * rx * 0.35];
      level(g, i < 5 ? 3 : i < 10 ? 2 : 1);
      px(g, p[0], p[1], i < 3 ? C('white', 8) : C('blossom', 4));
      if (i < 5) px(g, p[0], p[1] + 1, C('violet', 3));
    }
  }
  g.globalAlpha = 1;
}

/** Sample 9's petals: a turning ring of petals on the ground, rising in a spiral round the body to the peak. */
function petalRing(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.2 || s.t > 1.9) return;
  const fall = since(s.t, 1.0);
  for (let i = 0; i < 22; i++) {
    const a = (i / 22) * Math.PI * 2 + s.t * 1.6;
    if (Math.sin(a) > 0 !== front) continue;
    const up = clamp01((s.t - 0.3 - (i % 7) * 0.05) / 0.7);
    const r = 20 - up * 8 + (fall > 0 ? fall * 3 : 0);
    const x = s.a[0] + Math.cos(a) * r;
    const y = s.a[1] + Math.sin(a) * r * 0.5 - up * (s.chest * 2) + (fall > 0 ? fall * 2 : 0);
    level(g, fall < 3 ? 3 : fall < 6 ? 2 : 1);
    g.fillStyle = i % 2 ? C('blossom', 4) : C('blossom', 5);
    g.fillRect(Math.round(x), Math.round(y), (i + Math.floor(s.t * FPS)) % 2 ? 3 : 2, (i + Math.floor(s.t * FPS)) % 2 ? 2 : 3);
    px(g, x, y, C('blossom', 2));
  }
  g.globalAlpha = 1;
}

/** A hit star unless the scene is quiet. */
function hitStar(g: G, s: Scene, p: V, e: number, ramp: string, big = 1): void {
  if (!s.quiet) starOf(g, p, e, ramp, big);
}
